import { useParams } from "react-router"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { CreatableReportTemplate, ReportTemplate, UpdatableReportTemplate } from "@nova/schemas"
import {
    useReportTemplate,
    useUpdateReportTemplate,
} from "features/pilot-tracking/report-templates/ReportTemplateQueries"
import { ReportTemplateForm } from "features/pilot-tracking/report-templates/ReportTemplateForm"
import { useRouter } from "pages"
import { getGenericWorkspacePage } from "../WorkspaceRoutes"
import { IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"

export const EditReportTemplatePage = getGenericWorkspacePage({
    Header: EditReportTemplatePageHeader,
    Content: EditReportTemplatePageContent,
    maxWidth: 1200,
})

const toCreatable = (t: ReportTemplate): CreatableReportTemplate => ({
    name: t.name,
    description: t.description,
    report_type: t.reportType,
    date_range: t.dateRange,
    filters: t.filters,
    format_config: t.formatConfig,
    schedule: t.schedule,
    notification_config: t.notificationConfig,
})

function EditReportTemplatePageHeader() {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <div className="flex items-center h-full gap-4">
            <router.Link to={{ path: "/fleet/reports" }}>
                <IconButton>
                    <Icons.Icon Icon={Icons.ArrowLeft} />
                </IconButton>
            </router.Link>
            <h2 className="text-gray-0 telegraf">Edit Report Template</h2>
        </div>
    )
}

function EditReportTemplatePageContent() {
    const dispatch = useAppDispatch()
    const { reportTemplateId } = useParams<{ reportTemplateId: string }>()
    const { data: template, isLoading } = useReportTemplate(reportTemplateId!)
    const { updateReportTemplate } = useUpdateReportTemplate()
    const router = useRouter("/workspace/:workspaceId")

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }
    if (!template) return null

    const handleSubmit = async (payload: CreatableReportTemplate) => {
        const data: UpdatableReportTemplate = {
            ...payload,
        }

        const updated = await updateReportTemplate({ reportTemplateId: template.id, data })

        if (!updated) {
            dispatch(
                setGlobalNotificationContext({
                    message: `Failed to update ${payload.name}. Please try again.`,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 3000,
                })
            )
            return
        }

        dispatch(
            setGlobalNotificationContext({
                message: `${updated.name} has been updated.`,
                status: GlobalNotificationStatus.success,
                timeoutInMs: 3000,
            })
        )
    }

    return (
        <div className="w-full flex justify-center h-full overflow-hidden pb-4">
            <ReportTemplateForm
                initialValues={toCreatable(template)}
                onCancel={() => router.navigate({ path: "/fleet/reports" })}
                onSubmit={handleSubmit}
                operation="edit"
                afterSubmit={() => router.navigate({ path: "/fleet/reports" })}
            />
        </div>
    )
}
